import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  buildToken, renderQRSticker, renderLoginSticker, downloadCanvasPNG,
  printCanvases,
} from "@/lib/qrLabel";
import {
  Download, Printer, QrCode, Sparkles, ClipboardCheck, UserCheck,
  Bluetooth, Check, Loader2, RotateCw, Save,
} from "lucide-react";
import api from "@/lib/api";
import {
  isBluetoothSupported, isPrinterConnected, connectPrinter,
  reconnectPrinter, printCanvas as btPrint,
} from "@/lib/btPrinter";

const QR_DEFAULT = { widthMm: 80, heightMm: 60, dpi: 203, rotate: false };

/**
 * Per-facility QR code panel with Bluetooth thermal print support.
 *
 * Each card mirrors the FormQRModal flow:
 *   • label renders at the physical mm size the sticker roll actually is
 *     (via `renderQRSticker` / `renderLoginSticker` — TSPL 203 dpi);
 *   • Width / Height / Rotate 90° are editable per-card so operators can
 *     dial in a one-off size for this print run;
 *   • "Save size" persists per-facility (hygiene + cleanliness share it)
 *     or per-site (visitor), so future prints come up with the correct
 *     size out of the box;
 *   • Print sends the label directly to the connected Bluetooth thermal
 *     printer — never the browser's print dialog.
 *
 * The size hierarchy is: saved on this record → Brand Settings default
 * → hardcoded 80×60. "Print all" still uses the A4 sheet flow (browser
 * dialog) because operators use it for reference / office printing, not
 * for handheld Bluetooth rolls.
 */
export default function SiteQRPanel({ site, facilities }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/food-prep/print-config");
        if (!cancelled) setCfg(data);
      } catch {
        if (!cancelled) setCfg({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hygieneFacs = useMemo(
    () => (facilities || []).filter((f) => f.daily_hygiene_enabled),
    [facilities],
  );
  const cleanlinessFacs = useMemo(
    () => (facilities || []).filter((f) => f.daily_cleanliness_enabled),
    [facilities],
  );

  // Refs to all card canvases — used by "Print all" (A4 sheet).
  const cardsRef = useRef([]);
  const registerCard = (key, canvas, title, subtitle) => {
    cardsRef.current = cardsRef.current.filter((c) => c.key !== key);
    if (canvas) cardsRef.current.push({ key, canvas, title, subtitle });
  };

  const printAllSheet = () => {
    if (cardsRef.current.length === 0) return;
    printCanvases(cardsRef.current, `QR labels — ${site.name}`);
  };

  const totalQRs = hygieneFacs.length + cleanlinessFacs.length + 1;  // +1 visitor

  return (
    <div className="vb-card p-4 mt-4" data-testid={`qr-panel-${site.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="vb-heading text-base m-0" style={{ color: "var(--vb-text-2)" }}>
          <QrCode size={16} className="inline-block mr-1.5 -mt-0.5" />
          QR Codes for this site
          <span className="ml-2 text-xs opacity-60" data-testid={`qr-total-${site.id}`}>
            ({hygieneFacs.length} hygiene · {cleanlinessFacs.length} cleanliness · 1 visitor)
          </span>
        </h3>
        {totalQRs > 0 && (
          <button
            className="vb-btn ghost text-xs"
            onClick={printAllSheet}
            title="Prints every QR on an A4 sheet through the browser print dialog — good for office / reference printing."
            data-testid={`print-all-qrs-${site.id}`}
          >
            <Printer size={13} /> A4 sheet · Print all ({totalQRs})
          </button>
        )}
      </div>

      <SectionHeader icon={ClipboardCheck} label={`Daily Hygiene · ${hygieneFacs.length}`} />
      {hygieneFacs.length === 0 ? (
        <EmptyHint text="No facilities at this site have Daily Hygiene enabled. Enable it on a facility above to generate its QR." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4" data-testid={`hyg-qrs-${site.id}`}>
          {hygieneFacs.map((f) => (
            <FacilityQRCard
              key={`hyg-${f.id}`}
              cardKey={`hyg-${f.id}`}
              site={site} facility={f}
              tokenKind="fac_hyg"
              icon={ClipboardCheck}
              moduleLabel="Daily Hygiene"
              fileBase={`QR_Hygiene_${f.name}_${site.name}`}
              cfg={cfg}
              onCanvas={registerCard}
            />
          ))}
        </div>
      )}

      <SectionHeader icon={Sparkles} label={`Daily Cleanliness · ${cleanlinessFacs.length}`} />
      {cleanlinessFacs.length === 0 ? (
        <EmptyHint text="No facilities at this site have Daily Cleanliness enabled. Enable it on a facility above to generate its QR." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`clean-qrs-${site.id}`}>
          {cleanlinessFacs.map((f) => (
            <FacilityQRCard
              key={`clean-${f.id}`}
              cardKey={`clean-${f.id}`}
              site={site} facility={f}
              tokenKind="fac_clean"
              icon={Sparkles}
              moduleLabel="Daily Cleanliness"
              fileBase={`QR_Cleanliness_${f.name}_${site.name}`}
              cfg={cfg}
              onCanvas={registerCard}
            />
          ))}
        </div>
      )}
      <SectionHeader icon={UserCheck} label="Visitor Registration · 1" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`visitor-qr-${site.id}`}>
        <VisitorRegQRCard
          cardKey={`visitor-${site.id}`}
          site={site}
          cfg={cfg}
          onCanvas={registerCard}
        />
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-70 mb-2 mt-2">
      <Icon size={12} /> {label}
    </div>
  );
}

function EmptyHint({ text }) {
  return <p className="text-xs opacity-60 mb-4">{text}</p>;
}

/**
 * Shared render/print controls for both the facility QR cards and the
 * visitor QR card. Extracted to a hook so the two cards can differ only
 * in the sticker renderer + the persistence endpoint without duplicating
 * a hundred lines of state, dirty tracking, connect/print logic.
 */
function useStickerControls({ cfg, initialDims, persist }) {
  const [widthMm, setWidthMm] = useState(null);
  const [heightMm, setHeightMm] = useState(null);
  const [rotate, setRotate] = useState(false);
  const [savedDims, setSavedDims] = useState({
    widthMm: initialDims.widthMm ?? null,
    heightMm: initialDims.heightMm ?? null,
    rotate: !!initialDims.rotate,
  });
  const [savingSize, setSavingSize] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(isPrinterConnected());

  // Seed once cfg loads. Priority: saved-on-record → Brand Settings → default.
  useEffect(() => {
    if (cfg == null) return;
    setWidthMm(initialDims.widthMm || cfg.qr_label_width_mm || QR_DEFAULT.widthMm);
    setHeightMm(initialDims.heightMm || cfg.qr_label_height_mm || QR_DEFAULT.heightMm);
    setRotate(initialDims.rotate != null ? !!initialDims.rotate : !!cfg.qr_label_rotate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const dimsLoaded = widthMm != null && heightMm != null;
  const qrDims = useMemo(() => ({
    widthMm: Number(widthMm) || QR_DEFAULT.widthMm,
    heightMm: Number(heightMm) || QR_DEFAULT.heightMm,
    dpi: (cfg && cfg.label_dpi) || QR_DEFAULT.dpi,
    rotate,
  }), [widthMm, heightMm, rotate, cfg]);

  const printerCfg = useMemo(() => ({
    ...(cfg || {}),
    label_width_mm: qrDims.widthMm,
    label_height_mm: qrDims.heightMm,
    label_dpi: qrDims.dpi,
    printer_command: (cfg && cfg.printer_command) || "tspl",
    printer_gap_mm: (cfg && cfg.qr_label_gap_mm != null) ? cfg.qr_label_gap_mm
      : ((cfg && cfg.printer_gap_mm != null) ? cfg.printer_gap_mm : 2),
  }), [cfg, qrDims]);

  const isDirty = dimsLoaded && (
    Number(widthMm) !== Number(savedDims.widthMm ?? cfg?.qr_label_width_mm ?? QR_DEFAULT.widthMm) ||
    Number(heightMm) !== Number(savedDims.heightMm ?? cfg?.qr_label_height_mm ?? QR_DEFAULT.heightMm) ||
    Boolean(rotate) !== Boolean(savedDims.rotate ?? cfg?.qr_label_rotate ?? false)
  );

  const saveSize = async () => {
    if (!dimsLoaded) return;
    setSavingSize(true);
    try {
      await persist({ width_mm: Number(widthMm), height_mm: Number(heightMm), rotate: !!rotate });
      setSavedDims({ widthMm: Number(widthMm), heightMm: Number(heightMm), rotate: !!rotate });
      toast.success("Sticker size saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save size");
    } finally { setSavingSize(false); }
  };

  const ensureConnected = async () => {
    if (!isBluetoothSupported()) {
      throw new Error("Direct printing needs Chrome on an Android device.");
    }
    if (!isPrinterConnected()) {
      const ok = await reconnectPrinter(printerCfg);
      if (!ok) await connectPrinter(printerCfg);
    }
    setConnected(true);
  };

  const connect = async () => {
    setBusy(true);
    try { await ensureConnected(); toast.success("Printer connected"); }
    catch (e) { toast.error(e.message || "Couldn't connect"); }
    finally { setBusy(false); }
  };

  const btThermalPrint = async (canvas, qty = 1) => {
    setBusy(true);
    try {
      await ensureConnected();
      const q = Math.max(1, parseInt(qty, 10) || 1);
      await btPrint(canvas, printerCfg, q);
      toast.success(`Sent ${q} sticker${q === 1 ? "" : "s"} to the printer`);
    } catch (e) {
      toast.error(e.message || "Print failed");
    } finally { setBusy(false); }
  };

  return {
    widthMm, heightMm, rotate, setWidthMm, setHeightMm, setRotate,
    dimsLoaded, qrDims, isDirty, savingSize, saveSize,
    busy, connected, connect, btThermalPrint,
  };
}

/**
 * Inline size + print controls rendered underneath the QR preview.
 * Kept as a single component so both facility and visitor cards look
 * and behave identically.
 */
function PrintControls({ ctl, testKey, onPrint }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold opacity-80">Sticker size</span>
        <button
          className="vb-btn sm ghost"
          onClick={ctl.saveSize}
          disabled={!ctl.dimsLoaded || !ctl.isDirty || ctl.savingSize}
          title={ctl.isDirty ? "Save this size as the default for this QR"
                             : "Size already saved — edit width/height/rotate to enable"}
          data-testid={`qr-save-size-${testKey}`}
          style={{ padding: "3px 8px", fontSize: 11 }}
        >
          {ctl.savingSize ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Save
        </button>
      </div>
      <div className="flex items-end gap-1.5 flex-wrap">
        <label className="text-[11px]">
          <span className="block opacity-70 mb-0.5">W (mm)</span>
          <input
            type="number" min="15" max="150"
            value={ctl.widthMm ?? ""}
            onChange={(e) => ctl.setWidthMm(e.target.value === "" ? "" : Number(e.target.value))}
            className="vb-input"
            style={{ width: 62, padding: "3px 6px", fontSize: 12 }}
            data-testid={`qr-width-${testKey}`}
          />
        </label>
        <label className="text-[11px]">
          <span className="block opacity-70 mb-0.5">H (mm)</span>
          <input
            type="number" min="15" max="150"
            value={ctl.heightMm ?? ""}
            onChange={(e) => ctl.setHeightMm(e.target.value === "" ? "" : Number(e.target.value))}
            className="vb-input"
            style={{ width: 62, padding: "3px 6px", fontSize: 12 }}
            data-testid={`qr-height-${testKey}`}
          />
        </label>
        <label className="text-[11px] flex items-center gap-1 cursor-pointer select-none mb-1">
          <input
            type="checkbox"
            checked={ctl.rotate}
            onChange={(e) => ctl.setRotate(e.target.checked)}
            data-testid={`qr-rotate-${testKey}`}
            style={{ width: 13, height: 13 }}
          />
          <RotateCw size={11} /> Rotate
        </label>
      </div>
      <div
        className="flex items-center justify-between gap-1 rounded-md px-2 py-1"
        style={{ background: "rgba(0,0,0,0.03)" }}
      >
        <span className="text-[11px] flex items-center gap-1">
          {ctl.connected
            ? <><Check size={11} style={{ color: "#16a34a" }} /> Printer</>
            : <><Bluetooth size={11} /> No printer</>}
        </span>
        {!ctl.connected && (
          <button
            className="vb-btn ghost text-[11px]"
            onClick={ctl.connect}
            disabled={ctl.busy}
            data-testid={`qr-connect-${testKey}`}
            style={{ padding: "2px 6px" }}
          >
            {ctl.busy ? <Loader2 size={11} className="animate-spin" /> : <Bluetooth size={11} />}
            Connect
          </button>
        )}
      </div>
    </div>
  );
}

function FacilityQRCard({ cardKey, site, facility, tokenKind, icon: Icon, moduleLabel, fileBase, cfg, onCanvas }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);

  const ctl = useStickerControls({
    cfg,
    initialDims: {
      widthMm: facility.qr_label_width_mm,
      heightMm: facility.qr_label_height_mm,
      rotate: facility.qr_label_rotate,
    },
    persist: (body) => api.put(`/facilities/${facility.id}/qr-label`, body),
  });

  useEffect(() => {
    if (!ctl.dimsLoaded) return;
    let cancelled = false;
    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const token = buildToken(tokenKind, facility.id);
      const lines = [
        facility.name || "",
        `${moduleLabel} · ${site.name || ""}`,
        "Vanguard Beacon",
      ];
      await renderQRSticker(canvas, token, lines, ctl.qrDims);
      if (cancelled) return;
      setReady(true);
      onCanvas?.(cardKey, canvas, facility.name, `${moduleLabel} · ${site.name || ""}`);
    })();
    return () => { cancelled = true; onCanvas?.(cardKey, null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenKind, facility.id, ctl.qrDims, ctl.dimsLoaded]);

  const download = () => downloadCanvasPNG(canvasRef.current, fileBase);
  const print = () => ctl.btThermalPrint(canvasRef.current, 1);

  return (
    <div className="vb-card p-3" data-testid={`qr-card-${cardKey}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} style={{ color: "var(--vb-text-2)" }} />
        <strong className="text-sm truncate" title={facility.name}>{facility.name}</strong>
      </div>
      <div className="flex justify-center mb-2" style={{ background: "#f5f1ea", padding: 10, borderRadius: 8 }}>
        <canvas
          ref={canvasRef}
          style={{ maxWidth: "100%", width: "100%", height: "auto", background: "#fff" }}
          data-testid={`qr-card-canvas-${cardKey}`}
        />
      </div>
      <p className="text-[11px] opacity-60 mb-2">{moduleLabel} · {site.name}</p>
      <PrintControls ctl={ctl} testKey={cardKey} />
      <div className="flex gap-1 justify-end mt-2">
        <button className="vb-btn ghost text-xs" onClick={download} disabled={!ready} data-testid={`qr-download-${cardKey}`}>
          <Download size={12} /> PNG
        </button>
        <button className="vb-btn text-xs" onClick={print} disabled={!ready || ctl.busy || !ctl.dimsLoaded} data-testid={`qr-print-${cardKey}`}>
          {ctl.busy ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
          Print
        </button>
      </div>
    </div>
  );
}

/**
 * Visitor Registration QR — one per site. Unlike facility QRs (which
 * encode an opaque VBE1 token consumable only by the in-app scanner),
 * this QR encodes a plain public URL so a walk-in visitor's phone camera
 * can open the form directly.
 */
function VisitorRegQRCard({ cardKey, site, cfg, onCanvas }) {
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [url, setUrl] = useState("");

  const ctl = useStickerControls({
    cfg,
    initialDims: {
      widthMm: site.visitor_qr_label_width_mm,
      heightMm: site.visitor_qr_label_height_mm,
      rotate: site.visitor_qr_label_rotate,
    },
    persist: (body) => api.put(`/sites/${site.id}/visitor-qr-label`, body),
  });

  useEffect(() => {
    if (!ctl.dimsLoaded) return;
    let cancelled = false;
    (async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
      const target = `${base}/v/${site.id}`;
      setUrl(target);
      const lines = ["Visitor Registration", site.name || "", "Vanguard Beacon"];
      await renderLoginSticker(canvas, target, lines, ctl.qrDims);
      if (cancelled) return;
      setReady(true);
      onCanvas?.(cardKey, canvas, "Visitor Registration", site.name || "");
    })();
    return () => { cancelled = true; onCanvas?.(cardKey, null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id, site.name, ctl.qrDims, ctl.dimsLoaded]);

  const fileBase = `QR_Visitor_${site.name}`;
  const download = () => downloadCanvasPNG(canvasRef.current, fileBase);
  const print = () => ctl.btThermalPrint(canvasRef.current, 1);

  return (
    <div className="vb-card p-3" data-testid={`qr-card-${cardKey}`}>
      <div className="flex items-center gap-2 mb-2">
        <UserCheck size={14} style={{ color: "var(--vb-text-2)" }} />
        <strong className="text-sm truncate" title="Visitor Registration">Visitor Registration</strong>
      </div>
      <div className="flex justify-center mb-2" style={{ background: "#f5f1ea", padding: 10, borderRadius: 8 }}>
        <canvas
          ref={canvasRef}
          style={{ maxWidth: "100%", width: "100%", height: "auto", background: "#fff" }}
          data-testid={`qr-card-canvas-${cardKey}`}
        />
      </div>
      <p className="text-[11px] opacity-60 mb-1">Visitor Registration · {site.name}</p>
      <p className="text-[10px] opacity-50 break-all mb-2" data-testid={`qr-card-url-${cardKey}`}>{url}</p>
      <PrintControls ctl={ctl} testKey={cardKey} />
      <div className="flex gap-1 justify-end mt-2">
        <button className="vb-btn ghost text-xs" onClick={download} disabled={!ready} data-testid={`qr-download-${cardKey}`}>
          <Download size={12} /> PNG
        </button>
        <button className="vb-btn text-xs" onClick={print} disabled={!ready || ctl.busy || !ctl.dimsLoaded} data-testid={`qr-print-${cardKey}`}>
          {ctl.busy ? <Loader2 size={12} className="animate-spin" /> : <Printer size={12} />}
          Print
        </button>
      </div>
    </div>
  );
}
